import api from './api';
import { authManager, User } from './authManager';
import { UserRole, OrganizationRole } from '@/types/enums';

export interface LoginRequest {
  email: string;
  password: string;
}

export interface RegisterRequest {
  email: string;
  password: string;
  organizationName: string;
  selectedTier?: string;
}

export interface AuthResponse {
  token: string;
  accessToken?: string;
  refreshToken?: string;
  expiresIn?: number;
  user: {
    id: string;
    email: string;
    name?: string;
    role: UserRole;
    globalRole: UserRole;
    parentId?: string;
    adminId?: string;
    currentOrganizationId?: string;
    emailVerified?: boolean;
    hasPasskey?: boolean;
    availableOrganizations?: Array<{
      id: string;
      organizationId: string;
      organizationName: string;
      organizationSlug: string;
      role: OrganizationRole;
    }>;
  };
}

export const authService = {
  /**
   * Login with email and password
   */
  async login(credentials: LoginRequest): Promise<AuthResponse> {
    const response = await api.post('/auth/login', {
      ...credentials,
      deviceFingerprint: await getDeviceFingerprint(),
    });
    return response.data;
  },

  /**
   * Register a new account
   */
  async register(userData: RegisterRequest): Promise<AuthResponse> {
    const response = await api.post('/auth/register', {
      ...userData,
      deviceFingerprint: await getDeviceFingerprint(),
    });
    return response.data;
  },

  /**
   * Refresh the current session (legacy method - uses current access token)
   */
  async refresh(): Promise<AuthResponse> {
    const response = await api.post('/auth/refresh');
    return response.data;
  },

  /**
   * Refresh tokens using refresh token
   */
  async refreshTokens(refreshToken: string): Promise<{
    accessToken: string;
    refreshToken: string;
    expiresIn: number;
  }> {
    const response = await api.post('/auth/token/refresh', {
      refreshToken,
      deviceFingerprint: await getDeviceFingerprint(),
    });
    return response.data;
  },

  /**
   * Logout the current user
   */
  async logout(refreshToken?: string): Promise<void> {
    await api.post('/auth/logout', refreshToken ? { refreshToken } : {});
  },

  /**
   * Request password reset email
   */
  async forgotPassword(email: string): Promise<{ message: string }> {
    const response = await api.post('/auth/forgot-password', { email });
    return response.data;
  },

  /**
   * Reset password with token
   */
  async resetPassword(token: string, password: string): Promise<{ message: string }> {
    const response = await api.post('/auth/reset-password', { token, password });
    return response.data;
  },

  /**
   * Verify email address
   */
  async verifyEmail(token: string): Promise<{ message: string; email: string }> {
    const response = await api.post('/auth/verify-email', { token });
    return response.data;
  },

  /**
   * Resend verification email
   */
  async resendVerification(): Promise<{ message: string }> {
    const response = await api.post('/auth/resend-verification');
    return response.data;
  },

  /**
   * Get active sessions for the current user
   */
  async getSessions(): Promise<{
    sessions: Array<{
      id: string;
      deviceName: string | null;
      createdAt: string;
      expiresAt: string;
      isCurrent: boolean;
    }>;
  }> {
    const response = await api.get('/auth/sessions');
    return response.data;
  },

  /**
   * Revoke all sessions (logout everywhere)
   */
  async revokeAllSessions(): Promise<{ message: string }> {
    const response = await api.post('/auth/sessions/revoke-all');
    return response.data;
  },

  /**
   * Revoke a specific refresh token
   */
  async revokeToken(refreshToken: string): Promise<{ success: boolean }> {
    const response = await api.post('/auth/token/revoke', { refreshToken });
    return response.data;
  },

  /**
   * Exchange one-time auth code for tokens (used after email verification)
   */
  async exchangeCode(code: string): Promise<{
    accessToken: string;
    refreshToken: string;
    expiresIn: number;
  }> {
    const response = await api.post('/auth/exchange-code', { code });
    return response.data;
  },

  /**
   * Switch to a different organization
   */
  async switchOrganization(organizationId: string): Promise<AuthResponse & {
    currentOrganization: { id: string; name: string; slug: string };
  }> {
    const response = await api.post('/auth/switch-organization', { organizationId });
    return response.data;
  },

  /**
   * Get available organizations for the current user
   */
  async getOrganizations(): Promise<{
    organizations: Array<{
      id: string;
      organizationId: string;
      organizationName: string;
      organizationSlug: string;
      role: OrganizationRole;
      joinedAt: string;
    }>;
  }> {
    const response = await api.get('/auth/organizations');
    return response.data;
  },
};

// Helper function to generate device fingerprint
async function getDeviceFingerprint(): Promise<string> {
  const data = [
    navigator.userAgent,
    navigator.language,
    screen.width,
    screen.height,
    Intl.DateTimeFormat().resolvedOptions().timeZone,
    new Date().getTimezoneOffset(),
  ].join('|');

  // crypto.subtle requires HTTPS (or localhost)
  // Fall back to simple hash if not available
  if (!window.crypto?.subtle) {
    let hash = 0;
    for (let i = 0; i < data.length; i++) {
      const char = data.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash;
    }
    return Math.abs(hash).toString(16).padStart(8, '0');
  }

  const encoder = new TextEncoder();
  const hashBuffer = await crypto.subtle.digest('SHA-256', encoder.encode(data));
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
}

// Re-export authManager for components that need direct access
export { authManager };
export type { User };
